import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "../../sweetalert/Sweetalert";
import axios from "../../../api/axios";
import { destroySession } from "../../../helpers/Session";
import axiosaim from "../../../api/axiosaim";

const LeaveRequest = ({ departmentname, departmentslug, employeeUname }) => {
    const APPROVER_TYPE = 3;

    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const timeoutRef = useRef(null);

    const accesstoken = sessionStorage.getItem('accesstoken') || '';
    const aimtoken = sessionStorage.getItem('aimtoken') || '';
    const creator = sessionStorage.getItem('uname') || '';
    
    const [employeeLeaveApproverList, setEmployeeLeaveApproverList] = useState([]);
    const [loadingLeaveRequestApprovers, setLoadingLeaveRequestApprovers] = useState(false);

    const APPROVER_QUERY_KEY = ['departmentLeaveRequestApprovers', { departmentslug, APPROVER_TYPE, employeeUname }];

    const { data: employees } = useQuery({
        queryKey: ['departmentEmployees', {departmentslug}],
        queryFn: async () => {
            const res = await axiosaim.get(`getuserlistsperdepartment?d=${departmentslug}`, {
                headers: { 'X-Aim-Token': aimtoken }
            });
            return res.data;
        },
        enabled: !!departmentslug && !!aimtoken,
    });
    
    const { data: leaveRequestApprovers, isLoading: isLoadingLeaveRequestApprovers, error: errorLeaveRequestApprovers } = useQuery({
        queryKey: APPROVER_QUERY_KEY,
        queryFn: async () => {
            if (!employeeUname) return [];
            try {
                const res = await axios.get(`approvals?department=${departmentslug}&type=${APPROVER_TYPE}&employee=${employeeUname}`, {
                    headers: { 'Authorization': `Bearer ${accesstoken}` }
                });
                setLoadingLeaveRequestApprovers(false);
                return res.data.response;
            } catch (error) {
                setLoadingLeaveRequestApprovers(false);
            }
        },
        onError: (error) => {
            setLoadingLeaveRequestApprovers(false);
        },
        onSuccess: () => {
            setLoadingLeaveRequestApprovers(false);
        },
        enabled: !!departmentslug && !!accesstoken,
        staleTime: 0,
        retry: false
    });
    
    useEffect(() => {
        if (leaveRequestApprovers && leaveRequestApprovers.length > 0) {
            setEmployeeLeaveApproverList(leaveRequestApprovers);
        } else {
            setEmployeeLeaveApproverList([]);
        }
    }, [leaveRequestApprovers]);
    
    useEffect(() => {
        if (errorLeaveRequestApprovers) {
            if (errorLeaveRequestApprovers.response?.status === 404) {
                setEmployeeLeaveApproverList([]);
            } else {
                setEmployeeLeaveApproverList([]);
            }
        }
    }, [errorLeaveRequestApprovers]);

    const handleAddEmployee = () => {
        const newEmployee = {
            id: `employee-${employeeLeaveApproverList?.length + 1}`,
            sequence: employeeLeaveApproverList?.length + 1,
            uname: `employee-${employeeLeaveApproverList?.length + 1}`,
            fname: '',
            lname: '',
            email: '',
            status: true
        };
        setEmployeeLeaveApproverList(prevState => [...prevState, newEmployee]);
    };

    const departmentLeaveRequestApproverMutation = useMutation({
        mutationFn: async (approverData) => {
            const res = await axios.post('approvals', approverData, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data;
        },
        onSuccess: (data) => {
            toast.fire({ icon: 'success', title: data.response });
            queryClient.invalidateQueries(APPROVER_QUERY_KEY);
        },
        onError: (error) => {
            const errorMessage = error.response?.data.response || "Something went wrong while saving leave request approver details!";
            toast.fire({ icon: 'error', title: errorMessage });

            if (error.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const handleApproverStatus = (id, sequence) => {
        setEmployeeLeaveApproverList(prevState =>
            prevState.map(emp =>
                emp.sequence === sequence
                ? { ...emp, status: !emp.status }
                : emp
            )
        );
        const employee = employeeLeaveApproverList?.find(item => item.id === id);
        
        if (employee && employee.fname !== "") {
            departmentLeaveRequestApproverMutation.mutate({
                id: employee.id,
                department: departmentname,
                departmentcode: departmentslug,
                uname: employee.uname,
                name: employee.name,
                email: employee.email,
                sequence: employee.sequence,
                status: !employee.status,
                type: APPROVER_TYPE,
                creator,
                employee: employeeUname
            });
        }
    };

    const handleSetApprover = (e, id, sequence) => {
        const uname = e.target.value;
        
        if (uname === "") {
            setEmployeeLeaveApproverList(prevState => prevState.filter(emp => emp.sequence !== sequence));
        } else {
            const selectedEmployee = employees?.find(item => item.uname === uname);
            if (selectedEmployee) {
                const updatedEmployeeList = employeeLeaveApproverList?.map(emp =>
                    emp.sequence === sequence
                        ? { ...emp, ...selectedEmployee }
                        : emp
                );
                setEmployeeLeaveApproverList(updatedEmployeeList);
                
                const employee = updatedEmployeeList?.find(item => item.id === id);
                departmentLeaveRequestApproverMutation.mutate({
                    id: employee.id,
                    department: departmentname,
                    departmentcode: departmentslug,
                    uname: employee.uname,
                    name: `${employee.fname} ${employee.lname}`,
                    email: employee.email,
                    sequence: employee.sequence,
                    status: 1,
                    type: APPROVER_TYPE,
                    creator,
                    employee: employeeUname
                });
            }
        }
    };

    const departmentLeaveRequestApproverDeleteMutation = useMutation({
        mutationFn: async (id) => {
            const res = await axios.delete(`approvals/${id}?uname=${creator}&employee=${employeeUname}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data;
        },
        onSuccess: (data) => {
            toast.fire({ icon: 'success', title: data.response });
            queryClient.invalidateQueries(APPROVER_QUERY_KEY);
        },
        onError: (error) => {
            const errorMessage = error.response?.data.response || "Something went wrong while deleting leave request approver details!";
            toast.fire({ icon: 'error', title: errorMessage });

            if (error.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const handleRemoveEmployee = (id) => {
        const employee = employeeLeaveApproverList?.find(item => item.id === id);

        if (employee && employee.fname !== "") {
            departmentLeaveRequestApproverDeleteMutation.mutate(employee.id, creator);
        }
        
        setEmployeeLeaveApproverList(prevState => prevState.filter(emp => emp.id !== id));
    };

    return (
        <div className="content">
            {isLoadingLeaveRequestApprovers || loadingLeaveRequestApprovers ? (
                [...Array(2)].map((_, i) => (
                    <div key={i} className="employee loading">
                        <div>
                            <div className="top">
                                <p>Approver 1</p>
                            </div>
                            <select>
                                <option value="ralejo">Resty Alejo</option>
                            </select>
                        </div>
                    </div>
                ))
            ) : (
                <>
                    {employeeLeaveApproverList?.map((employee, index) => (
                        <div className={`employee ${employee.status ? '' : 'inactive'}`} key={index}>
                            <div>
                                <div className="top">
                                    <p>{`Approver ${employee.sequence}`}</p>
                                    <div className="switch">
                                        <input
                                        type="checkbox"
                                        id={`lr-toggle-${index + 1}`}
                                        className="switch-input"
                                        title="Active/Inactive"
                                        checked={employee.status}
                                        onChange={() => handleApproverStatus(employee.id, index + 1)}
                                        />
                                        <label htmlFor={`lr-toggle-${index + 1}`} className="switch-label"></label>
                                    </div>
                                </div>
                                <select onChange={(e) => handleSetApprover(e, employee.id, index + 1)} value={employee.uname}>
                                    {employees?.length > 0 ? 
                                        (
                                            <>
                                                <option value=""></option>
                                                {employees?.reduce((acc, { department, uname, fname, lname, position_title, employee_id }) => {
                                                    if (!acc[department]) acc[department] = [];
                                                    acc[department].push({ uname, fname, lname, position_title, employee_id });
                                                    return acc;
                                                }, {}) && Object.entries(employees?.reduce((acc, { department, uname, fname, lname, position_title, employee_id }) => {
                                                    if (!acc[department]) acc[department] = [];
                                                    acc[department].push({ uname, fname, lname, position_title, employee_id });
                                                    return acc;
                                                }, {})).map(([department, employees]) => (
                                                    <optgroup key={department} label={department}>
                                                    {employees.map(({ uname, fname, lname }) => (
                                                        <option key={uname} value={uname}>
                                                        {`${fname} ${lname}`}
                                                        </option>
                                                    ))}
                                                    </optgroup>
                                                ))}
                                            </>
                                        ) : (
                                            employeeLeaveApproverList?.length > 0 && (
                                                <option key={employee.uname} value={employee.uname}>
                                                    {employee.fname} {employee.lname}
                                                </option>
                                            )
                                        )
                                    }
                                    
                                    {employeeLeaveApproverList?.length > 0 && employee?.length > 0 && !employees.some(emp => emp.uname === employee.uname) && (
                                        <option key={employee.uname} value={employee.uname}>
                                            {employee.name}
                                        </option>
                                    )}
                                </select>

                            </div>
                            {index > 0 && (
                                <button type="button" className="btn-remove-employee" title="REMOVE" onClick={() => handleRemoveEmployee(employee.id)}>
                                    <i className="fa fa-trash"></i>
                                </button>
                            )}
                        </div>
                    ))}

                    <div className="button-group">
                        {employeeLeaveApproverList?.length < employees?.length && (
                            <button type="button" className="btn-add-employee" onClick={handleAddEmployee}>
                                <i className="fa fa-plus"></i>
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default LeaveRequest;